import { DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { addDays, differenceInCalendarDays, startOfDay } from 'date-fns'
import { DatetimePickerComponent } from '../../common/datetime-picker/datetime-picker.component'
import { DATE_TIME_FORMAT, resolveLocalTimeZone } from '../../utils'
import { formatDayCount } from './retention-day-count'
import { RetentionTimelineComponent, RetentionTimelineView } from './retention-timeline/retention-timeline.component'

const QUICK_EXTENSIONS_IN_DAYS = [1, 3, 7]

/** The guide that explains what the two deletions are and what sits between them. */
const DELETION_LIFECYCLE_HELP_URL = 'https://help.wiseworks.ai/en/articles/409999-data-retention-deletion-policy'

const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000

/**
 * Rounded rather than truncated: the two deletion dates are a whole number of days apart in absolute
 * terms, but a daylight-saving change between them leaves them an hour short of that in local time,
 * which truncation would report as a day less.
 */
function daysBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / MILLISECONDS_IN_DAY)
}

/** The nearer of two ceilings, either of which may be missing where its cap is not set. */
function earliestOf(first: Date | null, second: Date | null): Date | null {
  if (first === null) {
    return second
  }
  return second === null || first < second ? first : second
}

function latestOf(first: Date, second: Date): Date {
  return first > second ? first : second
}

/**
 * How far back a date may be brought: to where it stood before the first extension, but never
 * before tomorrow, and never later than it already stands — a meeting that has nothing to take back
 * stays where it is.
 *
 * The server only refuses a date in the past. Tomorrow is the dialog's own, stricter floor: a date
 * later today would leave the owners a few hours between the email and the meeting disappearing, and
 * would itself be in the past by the time a dialog left open for a while was saved.
 */
function reductionFloor(current: Date, minimum: Date | null, earliestAllowed: Date): Date {
  const floor = minimum === null ? current : latestOf(new Date(minimum), earliestAllowed)
  return floor < current ? floor : current
}

/**
 * How far past the current deletion date the scale reaches for an organization that caps nothing:
 * there is no maximum to end it at, and a month of room is enough to see the markers move.
 */
const UNCAPPED_LOOKAHEAD_IN_DAYS = 30

/** How much of the scale is left empty past its last date, so a marker never sits on the edge. */
const SCALE_HEADROOM = 0.05

export type ExtendRetentionDialogData = {
  /** Named in the dialog so that the meeting being acted on is beyond doubt before saving. */
  callName: string
  /** When the meeting took place, where the timeline starts. */
  meetingDate: Date
  /** When the meeting is hidden from its organization as things stand. */
  currentSoftDeletionDate: Date
  /** When it is deleted for good as things stand. */
  currentDeletionDate: Date
  /** The furthest the deletion date may be pushed, or null when the organization sets no total. */
  maximumDeletionDate: Date | null
  /** The furthest the soft deletion date may be pushed by this one extension. */
  maximumSoftDeletionDate: Date | null
  /** The furthest it may ever be pushed, which is where the timeline's last tick sits. */
  maximumSoftDeletionDateInTotal: Date | null
  /** The caps as the organization sets them, which the dialog states rather than implies. */
  maxSingleExtensionInDays: number | null
  maxTotalExtensionInDays: number | null
  usedTotalExtensionInDays: number
  /**
   * Where each date stood before the first extension, as far back as it may be brought again; null
   * where the server had no deletion dates to measure from.
   */
  minimumSoftDeletionDate: Date | null
  minimumDeletionDate: Date | null
}

export type ExtendRetentionDialogResult = {
  deletionDate: Date
  softDeletionDate: Date
}

@Component({
  selector: 'app-extend-retention-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    TranslatePipe,
    TranslateDirective,
    DatetimePickerComponent,
    RetentionTimelineComponent,
  ],
  providers: [DatePipe],
  templateUrl: './extend-retention-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './extend-retention-dialog.component.scss',
})
export class ExtendRetentionDialogComponent {
  data: ExtendRetentionDialogData = inject<ExtendRetentionDialogData>(MAT_DIALOG_DATA)
  private translateService = inject(TranslateService)
  private datePipe = inject(DatePipe)

  // The picker reads and writes a plain Date, so what the user types is in their own time zone. Every
  // date here is shown in it, and it is named, rather than being quietly converted to London's.
  localTimeZone = resolveLocalTimeZone()

  readonly deletionLifecycleHelpUrl = DELETION_LIFECYCLE_HELP_URL

  // The dates reach the dialog as they came off the wire, so each is read into a Date of its own
  // before any arithmetic is done on it.
  private meetingDate = new Date(this.data.meetingDate)
  private currentSoftDeletionDate = new Date(this.data.currentSoftDeletionDate)
  private currentDeletionDate = new Date(this.data.currentDeletionDate)
  private maximumDeletionDate: Date | null = this.data.maximumDeletionDate === null ? null : new Date(this.data.maximumDeletionDate)
  private maximumSoftDeletionDate: Date | null = this.data.maximumSoftDeletionDate === null ? null : new Date(this.data.maximumSoftDeletionDate)
  private maximumSoftDeletionDateInTotal: Date | null =
    this.data.maximumSoftDeletionDateInTotal === null ? null : new Date(this.data.maximumSoftDeletionDateInTotal)

  private earliestAllowedDate = startOfDay(addDays(new Date(), 1))
  private softDeletionFloor = reductionFloor(this.currentSoftDeletionDate, this.data.minimumSoftDeletionDate, this.earliestAllowedDate)
  private deletionFloor = reductionFloor(this.currentDeletionDate, this.data.minimumDeletionDate, this.earliestAllowedDate)

  // Both open on the dates the meeting stands at now, so the timeline reads as unchanged and Save
  // waits until an extension has actually been chosen.
  private stagedSoftDeletionDate: Date = new Date(this.currentSoftDeletionDate)
  private stagedDeletionDate: Date = new Date(this.currentDeletionDate)

  /**
   * Whether the organization hides a meeting for a while before deleting it. Where it does not, there
   * is only one date to move and the soft deletion is left out of the dialog altogether.
   */
  get hasSoftDeletion(): boolean {
    return this.currentDeletionDate > this.currentSoftDeletionDate
  }

  get softDeletionDate(): Date {
    return this.stagedSoftDeletionDate
  }

  /**
   * Pushing the soft deletion past the deletion date carries the deletion date along with it: the
   * meeting cannot be hidden after it has already been deleted for good.
   */
  set softDeletionDate(date: Date) {
    this.stagedSoftDeletionDate = date < this.softDeletionFloor ? new Date(this.softDeletionFloor) : date
    if (this.stagedSoftDeletionDate > this.stagedDeletionDate) {
      this.stagedDeletionDate = new Date(this.stagedSoftDeletionDate)
    }
  }

  get deletionDate(): Date {
    return this.stagedDeletionDate
  }

  /**
   * The mirror image: pulling the deletion date back in brings the soft deletion with it.
   *
   * Where the organization hides nothing, the two are one and the same date — the meeting stays
   * visible right up to the moment it is deleted — so the one marker the dialog then draws moves
   * both. Moving this date alone there would hide the meeting on the day it was always going to be
   * hidden and only put off destroying it, which is the opposite of what extending is asked for.
   */
  set deletionDate(date: Date) {
    this.stagedDeletionDate = date < this.deletionFloor ? new Date(this.deletionFloor) : date
    if (!this.hasSoftDeletion || this.stagedSoftDeletionDate > this.stagedDeletionDate) {
      this.stagedSoftDeletionDate = new Date(this.stagedDeletionDate)
    }
  }

  /** How long the meeting stays recoverable between the two staged dates. */
  private get recoveryWindowInDays(): number {
    return daysBetween(this.stagedSoftDeletionDate, this.stagedDeletionDate)
  }

  get canSave(): boolean {
    return this.hasChanges && !this.isBeyondMaximum
  }

  /**
   * Either way counts: bringing a date back in takes back days an earlier extension added, which the
   * floors keep from going any further than that.
   */
  get hasChanges(): boolean {
    return (
      this.stagedDeletionDate.getTime() !== this.currentDeletionDate.getTime() ||
      this.stagedSoftDeletionDate.getTime() !== this.currentSoftDeletionDate.getTime()
    )
  }

  /**
   * Whether either date has been typed past what the organization allows.
   *
   * Both are checked: the soft deletion date answers to the per-extension cap as well as the total,
   * and it is the one that says how long the meeting stays visible.
   */
  get isBeyondMaximum(): boolean {
    return this.isSoftDeletionBeyondMaximum || this.isDeletionBeyondMaximum
  }

  get isSoftDeletionBeyondMaximum(): boolean {
    return this.maximumSoftDeletionDate !== null && this.stagedSoftDeletionDate > this.maximumSoftDeletionDate
  }

  // Asked of each field separately, so that a soft deletion date typed too far out does not put an
  // error under the deletion date as well, which is where the two caps differ most often.
  get isDeletionBeyondMaximum(): boolean {
    const ceiling = this.deletionCeiling
    return ceiling !== null && this.stagedDeletionDate > ceiling
  }

  /** Puts both markers back where the dialog found them. */
  resetChanges() {
    this.stagedSoftDeletionDate = new Date(this.currentSoftDeletionDate)
    this.stagedDeletionDate = new Date(this.currentDeletionDate)
  }

  /**
   * The presets on offer, which leave out any that one extension could never add: an organization
   * that rations three days a time has no use for a week-long preset, and greying one out only
   * raises the question of why it is there at all.
   */
  get quickExtensionsInDays(): number[] {
    const maxSingle = this.data.maxSingleExtensionInDays
    return maxSingle === null ? QUICK_EXTENSIONS_IN_DAYS : QUICK_EXTENSIONS_IN_DAYS.filter((days) => days <= maxSingle)
  }

  quickExtensionLabel(days: number): string {
    return `+${formatDayCount(this.translateService, days)}`
  }

  /**
   * Where a preset puts the two dates: it extends the retention period, so it moves the soft
   * deletion, and the deletion date only follows where the extension would otherwise carry the
   * meeting past it. An organization that hides nothing has the two dates on the same day, so there
   * the deletion date is what moves.
   */
  private quickExtensionTarget(days: number): { softDeletionDate: Date; deletionDate: Date } {
    const softDeletionDate = this.cappedTo(addDays(this.currentSoftDeletionDate, days), this.furthestSoftDeletionDate)
    return { softDeletionDate, deletionDate: softDeletionDate > this.currentDeletionDate ? softDeletionDate : this.currentDeletionDate }
  }

  /**
   * Whether the meeting has too little of its total allowance left for a preset to be added in full.
   *
   * The per-extension cap cannot put one out of reach: a preset longer than the increment is not
   * offered in the first place.
   */
  isQuickExtensionBlocked(days: number): boolean {
    const inTotal = this.maximumSoftDeletionDateInTotal
    return inTotal !== null && addDays(this.currentSoftDeletionDate, days) > inTotal
  }

  /** Empty for an extension that fits: there is then nothing to explain. */
  quickExtensionTooltip(days: number): string {
    if (!this.isQuickExtensionBlocked(days)) {
      return ''
    }
    return this.translateService.instant('CALLS.EXTEND_RETENTION.QUICK_EXTEND_BLOCKED_BY_TOTAL', {
      date: this.datePipe.transform(this.maximumSoftDeletionDateInTotal, DATE_TIME_FORMAT) ?? '',
    })
  }

  /**
   * A preset out of reach never reads as picked, however the dates got where they are: capping it to
   * what the allowance has left lands it on the same day as the increment, which used to light up a
   * week-long preset the moment the increment button was pressed.
   */
  isQuickExtensionSelected(days: number): boolean {
    return !this.isQuickExtensionBlocked(days) && this.isStagedAt(this.quickExtensionTarget(days))
  }

  private isStagedAt(target: { softDeletionDate: Date; deletionDate: Date }): boolean {
    return this.stagedSoftDeletionDate.getTime() === target.softDeletionDate.getTime() && this.stagedDeletionDate.getTime() === target.deletionDate.getTime()
  }

  applyQuickExtension(days: number) {
    if (!this.isQuickExtensionBlocked(days)) {
      const target = this.quickExtensionTarget(days)
      this.stagedSoftDeletionDate = target.softDeletionDate
      this.stagedDeletionDate = target.deletionDate
    }
  }

  /** Nothing to jump to for an organization that rations nothing per extension. */
  get hasIncrement(): boolean {
    return this.data.maxSingleExtensionInDays !== null
  }

  /** Names the organization's own increment, so the button says what it will do before it is pressed. */
  get incrementLabel(): string {
    return this.translateService.instant('CALLS.EXTEND_RETENTION.MAX_INCREMENT', {
      days: formatDayCount(this.translateService, this.data.maxSingleExtensionInDays ?? 0),
    })
  }

  // Asked of the dates themselves rather than through a preset, since this button applies as much of
  // the increment as the allowance has left and so lights up even where a whole one is out of reach.
  get isIncrementSelected(): boolean {
    return this.hasIncrement && this.isStagedAt(this.quickExtensionTarget(this.data.maxSingleExtensionInDays!))
  }

  /** One whole increment, or as much of it as the total still has room for. */
  applyIncrement() {
    const maxSingle = this.data.maxSingleExtensionInDays
    if (maxSingle !== null) {
      const target = this.quickExtensionTarget(maxSingle)
      this.stagedSoftDeletionDate = target.softDeletionDate
      this.stagedDeletionDate = target.deletionDate
    }
  }

  /** What each picker will accept, which is not the same date for the two of them. */
  get minimumSoftDate(): Date {
    return this.softDeletionFloor
  }

  get minimumDate(): Date {
    return this.deletionFloor
  }

  get maximumSoftDate(): Date | null {
    return this.maximumSoftDeletionDate
  }

  get maximumDate(): Date | null {
    return this.deletionCeiling
  }

  applySoftExtensionInDays(days: number) {
    this.softDeletionDate = this.cappedTo(addDays(this.currentSoftDeletionDate, days), this.furthestSoftDeletionDate)
  }

  applyDeletionExtensionInDays(days: number) {
    this.deletionDate = this.cappedTo(addDays(this.currentDeletionDate, days), this.furthestDeletionDate)
  }

  /**
   * Keeps a marker dragged to the end of its travel on the furthest date rather than an hour past it,
   * which is where the last calendar day of the extension lands when a daylight-saving change falls
   * between the marker's current date and that furthest date. An hour past what is allowed is still
   * past it, and would leave the dialog unable to save what it had just let you choose.
   */
  private cappedTo(date: Date, furthest: Date): Date {
    return date > furthest ? furthest : date
  }

  get timeline(): RetentionTimelineView {
    return {
      meetingDate: this.meetingDate,
      currentSoftDeletionDate: this.currentSoftDeletionDate,
      currentDeletionDate: this.currentDeletionDate,
      softDeletionDate: this.stagedSoftDeletionDate,
      deletionDate: this.stagedDeletionDate,
      maximumTotalDate: this.maximumSoftDeletionDateInTotal,
      maximumTotalTooltip: this.maximumTotalTooltip,
      limitsLabel: this.limitsLabel,
      windowEndDate: this.windowEndDate,
      hasSoftDeletion: this.hasSoftDeletion,
      softExtensionInDays: daysBetween(this.currentSoftDeletionDate, this.stagedSoftDeletionDate),
      deletionExtensionInDays: daysBetween(this.currentDeletionDate, this.stagedDeletionDate),
      minimumSoftExtensionInDays: -this.extensionInDaysBetween(this.softDeletionFloor, this.currentSoftDeletionDate),
      minimumDeletionExtensionInDays: -this.extensionInDaysBetween(this.deletionFloor, this.currentDeletionDate),
      maximumSoftExtensionInDays: this.extensionInDaysBetween(this.currentSoftDeletionDate, this.furthestSoftDeletionDate),
      maximumDeletionExtensionInDays: this.extensionInDaysBetween(this.currentDeletionDate, this.furthestDeletionDate),
      recoveryWindowInDays: this.recoveryWindowInDays,
    }
  }

  /**
   * Why the soft-deletion marker stops where it does, which the tick alone cannot say: it marks the
   * end of the whole allowance, while one extension reaches only as far as the increment.
   */
  private get maximumTotalTooltip(): string {
    const { maxSingleExtensionInDays, maxTotalExtensionInDays, usedTotalExtensionInDays } = this.data
    if (maxTotalExtensionInDays === null) {
      return this.translateService.instant('CALLS.EXTEND_RETENTION.TIMELINE_MAXIMUM_TOTAL_UNCAPPED', {
        single: formatDayCount(this.translateService, maxSingleExtensionInDays ?? 0),
      })
    }
    const used =
      usedTotalExtensionInDays === 0
        ? ''
        : ` ${this.translateService.instant('CALLS.EXTEND_RETENTION.TIMELINE_MAXIMUM_TOTAL_USED', {
            used: formatDayCount(this.translateService, usedTotalExtensionInDays),
          })}`
    const key = maxSingleExtensionInDays === null ? 'TIMELINE_MAXIMUM_TOTAL_TOOLTIP_NO_INCREMENT' : 'TIMELINE_MAXIMUM_TOTAL_TOOLTIP'
    return `${this.translateService.instant(`CALLS.EXTEND_RETENTION.${key}`, {
      total: formatDayCount(this.translateService, maxTotalExtensionInDays),
      single: formatDayCount(this.translateService, maxSingleExtensionInDays ?? 0),
    })}${used}`
  }

  /** The caps stated outright in the footer, so they never have to be inferred from the dates. */
  private get limitsLabel(): string {
    const { maxSingleExtensionInDays, maxTotalExtensionInDays, usedTotalExtensionInDays } = this.data
    const total =
      maxTotalExtensionInDays === null
        ? this.translateService.instant('CALLS.EXTEND_RETENTION.LIMITS_NO_TOTAL')
        : this.translateService.instant(usedTotalExtensionInDays === 0 ? 'CALLS.EXTEND_RETENTION.LIMITS_TOTAL' : 'CALLS.EXTEND_RETENTION.LIMITS_TOTAL_USED', {
            total: formatDayCount(this.translateService, maxTotalExtensionInDays),
            used: usedTotalExtensionInDays,
          })
    if (maxSingleExtensionInDays === null) {
      return this.translateService.instant('CALLS.EXTEND_RETENTION.LIMITS_TOTAL_ONLY', { total })
    }
    return this.translateService.instant('CALLS.EXTEND_RETENTION.LIMITS', {
      single: formatDayCount(this.translateService, maxSingleExtensionInDays),
      total,
    })
  }

  /**
   * The furthest each marker may be dragged. The soft-deletion date answers to the per-extension cap
   * as well as the total; the deletion date only lengthens the window a hidden meeting can be brought
   * back in, so the total alone binds it. Without a cap the scale itself ends the drag, and the
   * fields below stay open for a date further out than that.
   */
  private get furthestSoftDeletionDate(): Date {
    return this.maximumSoftDeletionDate ?? this.uncappedLookahead
  }

  private get furthestDeletionDate(): Date {
    return this.deletionCeiling ?? this.uncappedLookahead
  }

  /**
   * The last date the deletion date may be set to, which the total alone binds while there is a
   * recovery window for it to be lengthening.
   *
   * Where the organization hides nothing, that date is also the last day the meeting is visible, so
   * the per-extension cap has to bind it as well — otherwise the one marker such a meeting has could
   * be dragged the whole allowance in a single extension, which is what the cap is there to ration.
   */
  private get deletionCeiling(): Date | null {
    return this.hasSoftDeletion ? this.maximumDeletionDate : earliestOf(this.maximumSoftDeletionDate, this.maximumDeletionDate)
  }

  private get uncappedLookahead(): Date {
    const lookahead = addDays(this.currentDeletionDate, UNCAPPED_LOOKAHEAD_IN_DAYS)
    return this.stagedDeletionDate > lookahead ? this.stagedDeletionDate : lookahead
  }

  /**
   * How many days of extension it takes to carry a marker from one date to the other.
   *
   * Far enough to reach the date rather than as far as fits before it: the two are the same whenever
   * the dates share a time of day, and differ by a day when they do not — which is what a
   * daylight-saving change between them leaves behind, and what used to strand the marker a day short
   * of the maximum. Going one day over is what {@link cappedTo} is there for.
   */
  private extensionInDaysBetween(from: Date, to: Date): number {
    const days = Math.max(0, differenceInCalendarDays(to, from))
    return addDays(from, days) < to ? days + 1 : days
  }

  /**
   * Where the scale ends: on the organization's total where it sets one, and a month past the current
   * deletion date where it does not — widened if a date beyond that is typed into a field, so the
   * marker stays in view.
   */
  private get windowEndDate(): Date {
    // The deletion date's ceiling rather than the soft one's, since it is the later of the two
    // wherever an organization's total is shorter than its recovery window — and ending the scale on
    // the soft cap there would leave the deletion marker drawn off the end of it.
    const capped = this.maximumDeletionDate ?? this.maximumSoftDeletionDate
    if (capped !== null) {
      return this.withHeadroom(this.stagedDeletionDate > capped ? this.stagedDeletionDate : capped)
    }
    const lookahead = addDays(this.currentDeletionDate, UNCAPPED_LOOKAHEAD_IN_DAYS)
    return this.stagedDeletionDate > lookahead ? this.withHeadroom(this.stagedDeletionDate) : lookahead
  }

  private withHeadroom(date: Date): Date {
    const span = date.getTime() - this.meetingDate.getTime()
    return new Date(date.getTime() + span * SCALE_HEADROOM)
  }

  get dialogResult(): ExtendRetentionDialogResult {
    return { deletionDate: this.stagedDeletionDate, softDeletionDate: this.stagedSoftDeletionDate }
  }
}
